import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ChangeObject } from "diff";

export enum MitigationType {
	CONTRADICTORY = "contradictory",
	MISSING = "missing",
	FAVOURABLE = "favourable",
}

export interface IMitigation {
	risk_id: string;
	risk: string;
	consequence: string;
	solution: string;
	reasoning: string; // Reasoning generated by the LLM, why the change is required.
	comparison_type: MitigationType;
	reference: string; // original string for which the suggestion is generated.
	new_version: string; // updated string
	/*
	 * Rationale contains what is changed and why
	 * Rationale is added as comment too.
	 */
	rationale: string;
	standard_text: string[]; // playbook clause which caused the issues
	newClause: boolean;

	non_standard_page: number;
	accepted: boolean | undefined;
	updated_at: string;

	/*
	 * diff of reference and new_version strings
	 * (generated on the frontend)
	 */
	diff: ChangeObject<string>[];
	playbook_id_id: string;
	playbook_clause_id_id: string;
	topic: string;
	file_id_id: string;
}

export interface IKeyTermMitigations {
	key: string;
	values: Array<IMitigation>;
}

export interface IRiskCategoryData {
	/*
	 * risks count is needed because the each mitigation
	 * can have multiple suggestions
	 */
	riskCount: number;
	risks: Array<IKeyTermMitigations>;
}

export interface IPlaybookMetadata {
	id: string;
	name: string;
}

export type IAnalysisStore = {
	playbookMetadata?: IPlaybookMetadata;
	contradictory?: IRiskCategoryData;
	missing?: IRiskCategoryData;
	processed?: IRiskCategoryData;
};

const initialState: IAnalysisStore = {};

export type TAnalyzeQuery = Omit<IAnalysisStore, "playbookMetadata">;

const analysisSlice = createSlice({
	name: "analysis",
	initialState,
	reducers: {
		setRisks: (state, action: PayloadAction<TAnalyzeQuery>) => {
			state.contradictory = action.payload.contradictory;
			state.missing = action.payload.missing;
			state.processed = action.payload.processed;
		},

		processSuggestion: function (
			state,
			action: PayloadAction<{
				type: MitigationType;
				keyTermIdx: number;
				mitigationIdx: number;
				accept: boolean;
			}>
		) {
			const { type, keyTermIdx, mitigationIdx, accept } = action.payload;

			// shouldn't happen but anyways
			if (!state[type] || !state[type].risks[keyTermIdx]) return;

			const sourceType: IRiskCategoryData = state[type];
			const keyTermMitigations = sourceType.risks[keyTermIdx];
			const mitigation = keyTermMitigations.values[mitigationIdx];

			mitigation.updated_at = new Date().toString();
			mitigation.accepted = accept;

			// remove the mitigation from the keyterm because its processed now.
			keyTermMitigations.values.splice(mitigationIdx, 1);
			// when its processed then this type will have one less risk
			sourceType.riskCount -= 1;

			// If no mitigations left for this keyTerm, remove the keyTerm entry
			if (keyTermMitigations.values.length === 0) {
				sourceType.risks.splice(keyTermIdx, 1);
			}

			const processed = state.processed;

			// check if the keyterm of the mitigation exists in the processed array.
			const processedKeyTermIdx = processed.risks.findIndex((risk) => risk.key === keyTermMitigations.key);

			if (processedKeyTermIdx === -1) {
				processed.risks.push({
					key: keyTermMitigations.key,
					values: [mitigation],
				});
				processed.riskCount += 1;
			} else {
				processed.risks[processedKeyTermIdx].values.push(mitigation);
				processed.riskCount += 1;
			}
		},

		setPlaybookMetadata: (state, action: PayloadAction<Partial<IPlaybookMetadata>>) => {
			state.playbookMetadata = {
				...state.playbookMetadata,
				...action.payload,
			};
		},

		setMitigationNewVersion: function (
			state,
			action: PayloadAction<{
				type: MitigationType;
				keyTermIdx: number;
				mitigationIdx: number;
				updatedText: string;
				updatedDiff: ChangeObject<string>[];
			}>
		) {
			const { type, keyTermIdx, updatedText, mitigationIdx, updatedDiff } = action.payload;

			state[type].risks[keyTermIdx].values[mitigationIdx].diff = updatedDiff;
			state[type].risks[keyTermIdx].values[mitigationIdx].new_version = updatedText;
		},

		setMitigationRationale: function (
			state,
			action: PayloadAction<{
				type: MitigationType;
				keyTermIdx: number;
				mitigationIdx: number;
				updatedComment: string;
			}>
		) {
			const { type, keyTermIdx, mitigationIdx, updatedComment } = action.payload;

			state[type].risks[keyTermIdx].values[mitigationIdx].rationale = updatedComment;
		},

		undoProcessSuggestion: function (
			state,
			action: PayloadAction<{
				type: MitigationType;
				keyTermIdx: number;
				mitigationIdx: number;
			}>
		) {
			const { type, keyTermIdx, mitigationIdx } = action.payload;

			const processed = state.processed;

			const processedKeyTerm = processed.risks[keyTermIdx];
			const mitigation = processedKeyTerm.values.at(mitigationIdx);
			mitigation.accepted = null;

			processedKeyTerm.values.splice(mitigationIdx, 1);
			processed.riskCount -= 1;

			if (processedKeyTerm.values.length === 0) {
				processed.risks.splice(keyTermIdx, 1);
			}

			const targetCategory: IRiskCategoryData = state[type];
			const targetKeyTermIdx = targetCategory.risks.findIndex((risk) => risk.key === processedKeyTerm.key);

			if (targetKeyTermIdx === -1) {
				targetCategory.risks.push({
					key: processedKeyTerm.key,
					values: [mitigation],
				});

				targetCategory.riskCount += 1;
			} else {
				targetCategory.risks[targetKeyTermIdx].values.push(mitigation);
				targetCategory.riskCount += 1;
			}
		},

		clearAnalysis: (state) => {
			state.contradictory = null;
			state.missing = null;
			state.processed = null;
		},
	},
});

export const {
	setPlaybookMetadata,
	setMitigationNewVersion,
	setRisks,
	setMitigationRationale,
	processSuggestion,
	undoProcessSuggestion,
	clearAnalysis,
} = analysisSlice.actions;

export default analysisSlice.reducer;
